import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { failureDetails, reasonKind, REASONS, reportText, storeLabel, type ReasonKind } from "./refreshDiagnostics";
import { openExternal } from "./telegram";
import type { RefreshJob, WishlistGame } from "./types";

export interface ReportProps {
  job: RefreshJob;
  games: WishlistGame[];
  retrying: number | null;
  error: string | null;
  onRetry: (gameId: number) => void;
  onOpenGame: (gameId: number) => void;
  onEditGame: (gameId: number) => void;
  onReload?: () => void;
}

export function RefreshReportContent({ job, games, retrying, error, onRetry, onOpenGame, onEditGame, onReload }: ReportProps) {
  const [query, setQuery] = useState("");
  const [store, setStore] = useState("");
  const [reason, setReason] = useState<ReasonKind | "">("");
  const [copied, setCopied] = useState<string | null>(null);
  const stores = [...new Set(job.failures.flatMap(item => failureDetails(item).map(detail => detail.store)).filter(Boolean))];
  const visible = job.failures.map(item => ({ ...item, details: failureDetails(item).filter(detail =>
    (!store || detail.store === store) && (!reason || reasonKind(detail.reason) === reason)),
  })).filter(item => item.details.length && item.title.toLocaleLowerCase().includes(query.toLocaleLowerCase()));
  const copy = async () => {
    try { await navigator.clipboard.writeText(reportText(job)); setCopied("Отчёт скопирован."); }
    catch { setCopied("Не удалось скопировать автоматически. Текст отчёта доступен ниже."); }
  };
  return <>
    <div className="report-summary" role="status">
      <strong>{job.status === "running" ? `Проверено ${job.completed} / ${job.total}` : job.status === "interrupted" ? "Проверка прервана" : "Проверка завершена"}</strong>
      <p>{job.status === "running" ? `Сейчас: ${job.current_title ?? "подготовка"}. ` : ""}Проверено игр: {job.completed} из {job.total}. Игр с ошибками: {job.failed_games}.</p>
      <p>Во время общей проверки обновлено источников: {job.updated_sources ?? 0}; игр с изменениями: {job.changed}.</p>
      <p>Успешные обновления уже сохранены. Ошибка одного магазина не отменяет результаты остальных.</p>
      {job.status === "interrupted" && job.current_title ? <p>Прервано на игре «{job.current_title}». Её источники могли обновиться частично.</p> : null}
    </div>
    <div className="report-filters">
      <label>Игра<input value={query} onChange={event => setQuery(event.target.value)} placeholder="Найти в отчёте" /></label>
      <label>Магазин<select value={store} onChange={event => setStore(event.target.value)}><option value="">Все магазины</option>{stores.map(value => <option value={value} key={value}>{storeLabel(value)}</option>)}</select></label>
      <label>Причина<select value={reason} onChange={event => setReason(event.target.value as ReasonKind | "")}><option value="">Все причины</option>{Object.entries(REASONS).map(([key, value]) => <option key={key} value={key}>{value[0]}</option>)}</select></label>
    </div>
    {error ? <p role="alert" className="report-error">{error}</p> : null}
    <div className="report-games">
      {visible.map(item => {
        const game = games.find(value => value.id === item.game_id);
        return <article className="report-game" key={item.game_id}>
          <h3>{item.title}</h3>
          <ul>{item.details.map((detail, index) => {
            const offer = game?.offers.find(value => value.store === detail.store && (!detail.offer_id || value.id === detail.offer_id));
            return <li key={`${detail.store}:${index}`}><strong>{storeLabel(detail.store) || "Проверка игры"}</strong><p>{detail.reason}</p><p className="report-hint">{REASONS[reasonKind(detail.reason)][1]}</p>
              {offer ? <a href={offer.url} target="_blank" rel="noreferrer" onClick={event => { event.preventDefault(); openExternal(offer.url); }}>Открыть источник ↗</a> : null}</li>;
          })}</ul>
          <div className="report-actions">
            <button type="button" title="Повторить все неудавшиеся источники этой игры" disabled={job.status === "running" || retrying !== null || !game} onClick={() => onRetry(item.game_id)}>{retrying === item.game_id ? "Проверяем…" : "Повторить ошибки"}</button>
            <button type="button" disabled={!game} onClick={() => onEditGame(item.game_id)}>Изменить источники</button>
            <button type="button" disabled={!game} onClick={() => onOpenGame(item.game_id)}>К игре</button>
          </div>
          {!game ? <p className="report-hint">Игра удалена из Wishlist.</p> : null}
        </article>;
      })}
      {!visible.length ? <p className="report-empty">{job.failures.length ? "По выбранным фильтрам ошибок нет." : job.status === "running" ? "Пока ошибок нет. Проверка продолжается." : "Нерешённых ошибок нет."}</p> : null}
    </div>
    {job.resolved_failures?.length ? <details className="report-resolved"><summary>После повтора без ошибок: {job.resolved_failures.length}</summary><ul>{job.resolved_failures.map(item => <li key={item.game_id}>{item.title}</li>)}</ul></details> : null}
    <div className="report-export"><div className="report-actions"><button type="button" onClick={() => void copy()}>Копировать отчёт</button>{onReload ? <button type="button" disabled={retrying !== null} onClick={onReload}>Обновить отчёт</button> : null}</div>{copied ? <p role="status">{copied}</p> : null}<details><summary>Текст отчёта</summary><textarea aria-label="Текст отчёта" readOnly value={reportText(job)} onFocus={event => event.currentTarget.select()} /></details></div>
  </>;
}

export function RefreshReport({ onClose, ...props }: ReportProps & { onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const element = dialog.current;
    const overflow = document.body.style.overflow;
    element?.showModal(); document.body.style.overflow = "hidden";
    return () => { element?.close(); document.body.style.overflow = overflow; };
  }, []);
  return createPortal(<dialog ref={dialog} className="refresh-report" aria-labelledby="refresh-report-title" onCancel={event => { event.preventDefault(); onClose(); }}>
    <header><div><span className="eyebrow">Проверка цен · №{props.job.id}</span><h2 id="refresh-report-title">Результаты обновления</h2></div><button type="button" aria-label="Закрыть отчёт" onClick={onClose}>×</button></header>
    <RefreshReportContent {...props} />
  </dialog>, document.body);
}
